import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/gestionale/components/ui/dialog";
import { Button } from "@/gestionale/components/ui/button";
import { Input } from "@/gestionale/components/ui/input";
import { Label } from "@/gestionale/components/ui/label";
import { Switch } from "@/gestionale/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/gestionale/components/ui/select";
import type { Tables, TablesInsert } from "@/gestionale/integrations/supabase/types";

type Sponsor = Tables<"sponsors">;

interface SponsorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sponsor: Sponsor | null;
  onSave: (sponsor: TablesInsert<"sponsors"> & { id?: string }) => void;
  isSaving: boolean;
}

const EMPTY_FORM = {
  name: "",
  tier: "argento",
  logo_url: "",
  website_url: "",
  telefono: "",
  active: true,
  data_scadenza: "",
  sort_order: 0,
};

export default function SponsorDialog({ open, onOpenChange, sponsor, onSave, isSaving }: SponsorDialogProps) {
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => {
    if (sponsor) {
      setForm({
        name: sponsor.name || "",
        tier: sponsor.tier || "argento",
        logo_url: sponsor.logo_url || "",
        website_url: sponsor.website_url || "",
        telefono: sponsor.telefono || "",
        active: sponsor.active ?? true,
        data_scadenza: sponsor.data_scadenza || "",
        sort_order: sponsor.sort_order ?? 0,
      });
    } else {
      setForm(EMPTY_FORM);
    }
  }, [sponsor, open]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      ...(sponsor ? { id: sponsor.id } : {}),
      name: form.name,
      tier: form.tier,
      logo_url: form.logo_url || null,
      website_url: form.website_url || null,
      telefono: form.telefono || null,
      active: form.active,
      data_scadenza: form.data_scadenza || null,
      sort_order: form.sort_order,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{sponsor ? "Modifica Sponsor" : "Nuovo Sponsor"}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Nome sponsor *</Label>
            <Input
              id="name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="tier">Livello</Label>
              <Select value={form.tier} onValueChange={(v) => setForm({ ...form, tier: v })}>
                <SelectTrigger id="tier">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="oro">Oro</SelectItem>
                  <SelectItem value="argento">Argento</SelectItem>
                  <SelectItem value="bronzo">Bronzo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="sort_order">Ordine visualizzazione</Label>
              <Input
                id="sort_order"
                type="number"
                value={form.sort_order}
                onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) })}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="logo_url">URL Logo</Label>
            <Input
              id="logo_url"
              value={form.logo_url}
              onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
              placeholder="https://..."
            />
            <p className="text-xs text-muted-foreground">
              Carica il file dallo storage tramite il pannello Admin del sito, poi incolla qui l'URL pubblico.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="website_url">Sito web</Label>
            <Input
              id="website_url"
              value={form.website_url}
              onChange={(e) => setForm({ ...form, website_url: e.target.value })}
              placeholder="https://..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="telefono">Telefono</Label>
              <Input
                id="telefono"
                value={form.telefono}
                onChange={(e) => setForm({ ...form, telefono: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="data_scadenza">Scadenza contratto</Label>
              <Input
                id="data_scadenza"
                type="date"
                value={form.data_scadenza}
                onChange={(e) => setForm({ ...form, data_scadenza: e.target.value })}
              />
            </div>
          </div>

          <div className="flex items-center justify-between rounded-lg border p-3">
            <div>
              <Label htmlFor="active">Sponsor attivo</Label>
              <p className="text-xs text-muted-foreground">Se disattivato, non compare sul sito pubblico</p>
            </div>
            <Switch
              id="active"
              checked={form.active}
              onCheckedChange={(v) => setForm({ ...form, active: v })}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annulla
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving ? "Salvataggio..." : sponsor ? "Salva modifiche" : "Crea sponsor"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}